import React, { useState, useEffect } from 'react';
import {
  Cloud,
  Code,
  Server,
  TrendingUp,
  Target,
  Settings,
  ArrowRight,
  Check
} from 'lucide-react';
import axios from 'axios';

const ServicesSection = () => {
  const [services, setServices] = useState([]);
  const [activeService, setActiveService] = useState(0);
  const [loading, setLoading] = useState(true);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8788';

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/content/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, [backendUrl]);

  if (loading) return null;
  if (services.length === 0) return null;

  const iconMap = {
    cloud: Cloud,
    code: Code,
    server: Server,
    'trending-up': TrendingUp,
    target: Target,
    settings: Settings
  };

  return (
    <section id="services" className="py-12 sm:py-16 lg:py-20 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <p className="text-silver/60 text-xs sm:text-sm font-mono tracking-widest uppercase mb-3 sm:mb-4">
            Our Expertise
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-silver mb-4 sm:mb-6 px-4">
            Services That Drive
            <span className="block gradient-text">Digital Excellence</span>
          </h2>
          <p className="text-gray-400 text-base sm:text-lg lg:text-xl max-w-3xl mx-auto leading-relaxed px-4">
            From cloud migrations to custom software development, we deliver enterprise-grade solutions
            that transform businesses and accelerate growth.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-16 sm:mb-20">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <div
                key={service.id}
                className={`group relative bg-gradient-to-br from-gray-900 to-black p-6 sm:p-8 rounded-xl border transition-all duration-500 cursor-pointer card-hover-effect ${activeService === index
                  ? 'border-silver shadow-2xl scale-105'
                  : 'border-gray-700 hover:border-gray-600'
                  }`}
                onClick={() => setActiveService(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setActiveService(index);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Select ${service.title} service`}
                aria-pressed={activeService === index}
              >
                {/* Service Icon */}
                <div className="flex items-center mb-6">
                  <div
                    className="p-3 rounded-lg mr-4"
                    style={{ backgroundColor: service.color }}
                  >
                    <IconComponent size={32} className="text-black" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-silver group-hover:text-white transition-colors duration-300">
                      {service.title}
                    </h3>
                  </div>
                </div>

                {/* Service Description */}
                <p className="text-gray-400 mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Service Features */}
                <div className="space-y-3 mb-6">
                  {Array.isArray(service.features) && service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-sm">
                      <Check size={16} className="text-silver mr-3 flex-shrink-0" />
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Learn More Button */}
                <button className="group/btn flex items-center text-silver hover:text-white transition-colors duration-300 font-medium focus:outline-none focus:ring-2 focus:ring-silver/50 rounded-lg px-2 py-1">
                  Learn More
                  <ArrowRight
                    size={16}
                    className="ml-2 group-hover/btn:translate-x-1 transition-transform duration-300"
                  />
                </button>

                {/* Hover Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-silver/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl pointer-events-none" />
              </div>
            );
          })}
        </div>

        {/* Process Flow */}
        <div className="glass-morphism rounded-2xl p-8 md:p-12">
          <h3 className="text-2xl md:text-3xl font-bold text-silver mb-8 text-center">
            Our Proven Process
          </h3>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', desc: 'Understand your challenges and objectives' },
              { step: '02', title: 'Strategy', desc: 'Develop customized solutions and roadmap' },
              { step: '03', title: 'Execute', desc: 'Implement with precision and transparency' },
              { step: '04', title: 'Optimize', desc: 'Monitor, refine, and scale for success' }
            ].map((phase, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-silver to-gray-400 flex items-center justify-center">
                  <span className="text-black font-bold text-lg">{phase.step}</span>
                </div>
                <h4 className="text-lg font-semibold text-silver mb-2">{phase.title}</h4>
                <p className="text-gray-400 text-sm leading-relaxed">{phase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;